import { gql, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";

import Header from "@/components/header-v2";
import LoadingAnimation from "@/components/loading-animation";
import ReviewModal from "@/components/review-modal";
import type {
  ReviewsPaginationType,
  ReviewType,
} from "@/components/review-modal/types";
import Timeslots from "@/components/timeslots";
import { Separator } from "@/components/ui/separator";
import { ESCAPE_UPDATE_FRAGMENT } from "@/graphql/mutations/use-update-escape";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";

import useGameStatus from "../search/hooks/use-game-status";
import AdditionalDetails from "./additional-details";
import AllDetails from "./all-details";
import AlsoConsider from "./also-consider";
import Awards from "./awards";
import BookButton from "./book-button";
import BookingsBanner from "./bookings-banner";
import CoverImages from "./cover-images";
import Details from "./details";
import EditorialReviews from "./editorial-reviews";
import Features from "./features";
import HandicapAccessibility from "./handicap-accessibility";
import Players from "./players";
import Promotions from "./promotions";
import Ratings, { type RatingType } from "./ratings";
import Reviews, { type ReviewsRef } from "./reviews";
import SuggestEdit from "./suggest-edit";
import type { GameType, UserGameType } from "./types";

export interface DataType {
  game: GameType;
  review?: ReviewType;
  reviews: ReviewsPaginationType;
}

export type GameProps = DataType & {
  path: string[];
};

export interface UserGameDataType {
  game: UserGameType;
}

export interface UserGameVariablesType {
  id: string;
}

// TODO: refactor this query to a reusable fetcher
export const GET_USER_GAME_FIELDS = gql`
  ${ESCAPE_UPDATE_FRAGMENT}

  query Game($id: ID!) {
    game(id: $id) {
      friendsEscaped {
        id
        escapeReview {
          id
          hasSummary
          overallRating
        }
        imageId
        username
      }
      friendsWishlisted {
        id
        imageId
        username
      }
      id
      isCompleted
      isSaved
      myEscape {
        ...EscapeUpdateFields
      }
      overallRating
    }
  }
`;

export default function Game({ game, path, review, reviews }: GameProps) {
  const reviewsRef = useRef<ReviewsRef>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { page, track } = useAnalytics();
  const { isOpen } = useGameStatus(game);
  const { isUser, queryContext } = useAuth();

  const { data: userGame } = useQuery<UserGameDataType, UserGameVariablesType>(
    GET_USER_GAME_FIELDS,
    { ...queryContext({ id: game.id }), skip: !isUser }
  );

  const handleReviewsFocus = () => {
    const { current } = reviewsRef;

    if (!current) {
      return;
    }

    current.scrollIntoView({ behavior: "smooth" });
  };

  const awardsProp = useMemo(
    () =>
      game.awards.map(
        ({ id, coarseUrl: url, displayTitle: title, category }) => ({
          id,
          source: category?.source?.awardName,
          title,
          url,
        })
      ),
    [game]
  );
  const ratingProps = useMemo(() => {
    const {
      communityRatingCount,
      communityScoreBucket,
      communityScorePercentages,
      componentScores,
    } = game;
    return {
      communityRatingCount,
      communityScoreBucket,
      communityScorePercentages,
      components: componentScores.reduce(
        (accum, { communityScoreBucket, question }) => {
          if (communityScoreBucket) {
            accum.push({
              label: question.shortQuestion || question.question,
              value: communityScoreBucket,
            });
          }
          return accum;
        },
        [] as RatingType[]
      ),
    };
  }, [game]);

  useEffect(() => {
    const { id, location, name } = game;
    page("Game", "Game Details", {
      company_id: location.company.id,
      company_name: location.company.name,
      game_id: id,
      game_name: name,
      location_id: location.id,
    });
    track("Bookable Experience Viewed", {
      company_id: location.company.id,
      company_name: location.company.name,
      game_id: id,
      game_name: name,
      location_id: location.id,
    });
  }, [game, page, track]);

  return (
    <>
      <Header mobileClassName="hidden" strategy="absolute" />
      <CoverImages game={game} />
      <BookingsBanner game={game} />
      <main className="relative z-1 mx-auto grid w-full max-w-347.5 grid-cols-1 px-6 pt-5 pb-12 md:grid-cols-5 md:gap-20 md:px-20 md:pt-0">
        <div className="col-span-3">
          <Details
            game={game}
            isOpen={isOpen}
            onReviewsClick={handleReviewsFocus}
            reviewCount={reviews.count || 0}
            userGame={userGame?.game}
          />
          <Timeslots
            className="md:hidden"
            game={game}
            ignoreTimezone={game.kind === "RER"}
            setLoading={setIsLoading}
          >
            <Separator className="my-5 bg-white/20 md:hidden" />
          </Timeslots>
          <Separator className="my-5 bg-white/20" />
          <Awards awards={awardsProp} />
          {userGame?.game.friendsEscaped &&
            userGame.game.friendsEscaped.length > 0 && (
              <Players title="Done by" users={userGame.game.friendsEscaped} />
            )}
          {userGame?.game.friendsWishlisted &&
            userGame.game.friendsWishlisted.length > 0 && (
              <Players
                title="Wishlisted"
                users={userGame.game.friendsWishlisted}
              />
            )}
          <Ratings {...ratingProps} />
          <AlsoConsider game={game} />
          <Reviews
            game={game}
            onFocusRequest={handleReviewsFocus}
            path={path}
            ref={reviewsRef}
            {...reviews}
          />
          <EditorialReviews reviews={game.editorialReviews || []} />
        </div>
        <div
          className="col-span-2 md:pt-(--bookings-offset)"
          style={
            {
              "--bookings-offset": `${539 + (game.hasBookings ? 44 : 0)}px`,
            } as React.CSSProperties
          }
        >
          {isLoading ? (
            <LoadingAnimation />
          ) : (
            <div className="flex flex-col items-stretch gap-5 md:gap-7.5">
              <Features className="md:order-6" game={game} />
              <BookButton className="md:order-1" game={game} />
              <Timeslots
                className="order-2 hidden md:block"
                game={game}
                ignoreTimezone={game.kind === "RER"}
                setLoading={setIsLoading}
              />
              {isOpen && (
                <Promotions
                  className="md:order-3"
                  coupons={game.coupons}
                  name={game.name}
                />
              )}
              <AllDetails
                className="md:order-4"
                gameSlug={game.slug}
                location={game.location}
              />
              <HandicapAccessibility
                className="md:order-5"
                handicapAccessibility={game.handicapAccessibility}
              />
              <AdditionalDetails
                additionalDetails={game.additionalDetails}
                className="md:order-7"
              />
              <SuggestEdit className="md:order-8" gameId={game.id} />
            </div>
          )}
        </div>
      </main>
      {review && <ReviewModal game={game} review={review} />}
    </>
  );
}
